import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { isAxiosError } from "axios";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useSWRConfig } from "swr";
import { useApiClient } from "@/hooks/useApiClient";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import {
  acquireReuse,
  getReuseQuote,
  type ReuseQuote,
} from "../api/acquireReuse";

/** 確認した見積りごとに冪等キーを保持し、確定後は法人詳細を再取得する。 */
export const CommercialReuseDialog: React.FC<{
  corporateNumber: string;
  companyName: string;
  returnCorporateNumber: string;
}> = ({ corporateNumber, companyName, returnCorporateNumber }) => {
  const { apiClient } = useApiClient();
  const clientRef = useRef(apiClient);
  clientRef.current = apiClient;
  const readOnly = useReadOnlyUser();
  const navigate = useNavigate();
  const { mutate } = useSWRConfig();
  const [quote, setQuote] = useState<ReuseQuote>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [generation, setGeneration] = useState(0);
  const pending = useRef(false);
  const operation = useRef("");
  const returnPath = /^\d{13}$/.test(returnCorporateNumber)
    ? `/corporate-master/${returnCorporateNumber}`
    : "/corporate-master";

  useEffect(() => {
    let active = true;
    operation.current = crypto.randomUUID();
    setQuote(undefined);
    setError("");
    setConflict(false);
    if (readOnly) return;
    void getReuseQuote(clientRef.current, corporateNumber)
      .then((value) => {
        if (active) setQuote(value);
      })
      .catch((reason: unknown) => {
        if (active) setError(errorMessage(reason));
      });
    return () => {
      active = false;
    };
  }, [corporateNumber, generation, readOnly]);

  const confirm = async (): Promise<void> => {
    if (pending.current || !quote?.quoteToken || readOnly || conflict) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      await acquireReuse(
        clientRef.current,
        corporateNumber,
        quote.quoteToken,
        operation.current
      );
    } catch (reason) {
      setError(errorMessage(reason));
      setConflict(isAxiosError(reason) && reason.response?.status === 409);
      pending.current = false;
      setBusy(false);
      return;
    }
    // 非表示の詳細キャッシュも失効させ、戻った画面で再取得する。
    await mutate(returnPath, undefined, { revalidate: false });
    navigate(returnPath, { replace: true });
  };

  const acquiredAt = quote?.sourceAcquiredAt
    ? new Date(quote.sourceAcquiredAt)
    : null;
  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      aria-labelledby="commercial-reuse-title"
    >
      <DialogTitle id="commercial-reuse-title">
        商業登記のリユース取得
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography>{companyName}</Typography>
          <Typography>会社法人等番号：{corporateNumber}</Typography>
          {readOnly ? (
            <Alert severity="error">
              この操作は現在のご契約ではご利用いただけません。
            </Alert>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          {!quote && !error && !readOnly ? (
            <Typography>候補を確認しています…</Typography>
          ) : null}
          {quote?.available ? (
            <>
              <Typography>
                元取得日：
                {acquiredAt && !Number.isNaN(acquiredAt.getTime())
                  ? format(acquiredAt, "yyyy/MM/dd HH:mm")
                  : "不明"}
              </Typography>
              {quote.closed ? (
                <Alert severity="warning">閉鎖された登記簿です。</Alert>
              ) : null}
              <Typography>取得内容：PDF＋解析結果</Typography>
              <Typography>
                料金：{quote.reusePrice?.toLocaleString("ja-JP")}円
              </Typography>
            </>
          ) : quote ? (
            <Alert severity="info">
              {quote.reason ?? "利用可能なリユース資料がありません。"}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={busy}
          onClick={() => {
            navigate(returnPath);
          }}
        >
          法人情報へ戻る
        </Button>
        {(conflict || (!quote && error)) && !readOnly ? (
          <Button
            onClick={() => {
              setGeneration((value) => value + 1);
            }}
          >
            候補を再確認
          </Button>
        ) : null}
        <Button
          variant="contained"
          disabled={
            busy ||
            readOnly ||
            conflict ||
            !quote?.available ||
            !quote.quoteToken ||
            quote.reusePrice == null
          }
          onClick={() => {
            void confirm();
          }}
        >
          {busy ? "取得中…" : "この料金で取得する"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const errorMessage = (reason: unknown): string => {
  if (
    isAxiosError<{ detail?: string }>(reason) &&
    typeof reason.response?.data.detail === "string"
  )
    return reason.response.data.detail;
  return "取得処理を確認できませんでした。再送してください。";
};
